import React from "react";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import type { VendorLicenseTaskDecision } from "@services/licensing/types";
import { useTranslation } from "react-i18next";
import { useDownloadLicenseDocument } from "./hooks/useDownloadLicenseDocument";
import { getFileExtensionFromUrl } from "./utils";

type StatusDetailsDrawerProps = {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  taskDecision?: VendorLicenseTaskDecision | null;
};

const SERVICE = "bff-licensing";

const isDefined = (v: unknown) => v !== undefined && v !== null;

export const hasStatusDetails = (taskDecision?: VendorLicenseTaskDecision | null) => {
  if (!taskDecision) return false;

  const approvalAttachmentUrl = taskDecision.approvalAttachmentUrl ?? null;
  const rejectionAttachmentUrl = taskDecision.rejectionAttachmentUrl ?? null;

  const hasApprovalDetails = Boolean(
    taskDecision.approvalNotes ||
      approvalAttachmentUrl ||
      taskDecision.startDate ||
      taskDecision.expiryDate ||
      isDefined(taskDecision.numberOfAccountsAllowed) ||
      taskDecision.companyId,
  );

  const hasRejectionDetails = Boolean(taskDecision.rejectionNotes || rejectionAttachmentUrl);

  return hasApprovalDetails || hasRejectionDetails;
};

const formatDate = (value: string | null | undefined, emptyLabel: string) => {
  if (!value) return emptyLabel;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${date.getFullYear()}`;
};

const StatusDetailsDrawer: React.FC<StatusDetailsDrawerProps> = ({
  isOpen,
  onClose,
  taskDecision,
}) => {
  const { t } = useTranslation();
  const { handleDownload, handlePreview } = useDownloadLicenseDocument();

  const approvalAttachmentUrl = taskDecision?.approvalAttachmentUrl ?? null;
  const rejectionAttachmentUrl = taskDecision?.rejectionAttachmentUrl ?? null;
  const hasApprovalDetails = Boolean(
    taskDecision?.approvalNotes ||
      approvalAttachmentUrl ||
      taskDecision?.startDate ||
      taskDecision?.expiryDate ||
      isDefined(taskDecision?.numberOfAccountsAllowed) ||
      taskDecision?.companyId,
  );

  const hasRejectionDetails = Boolean(taskDecision?.rejectionNotes || rejectionAttachmentUrl);

  const emptyValue = t("common.notApplicable", { defaultValue: "N/A" });

  const renderAttachment = (attachmentUrl: string, testIdPrefix: string) => {
    const ext = getFileExtensionFromUrl(attachmentUrl);
    const baseName = t("common.attachment", { defaultValue: "attachment" });
    const attachmentName = ext ? `${baseName}.${ext}` : baseName;

    return (
      <div>
        <div className="text-sm text-gray-500 font-semibold mb-1">{t("licensing.attachments")}</div>

        <div className="border rounded p-3 w-full border-[#F1F2F4]">
          <div className="flex justify-between items-center w-full">
            <a
              href={attachmentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline break-all cursor-pointer"
              data-testid={`${testIdPrefix}-link`}
              onClick={(e) => {
                e.preventDefault();
                handlePreview(SERVICE, attachmentUrl);
              }}
            >
              {attachmentName}
            </a>

            <button
              type="button"
              data-testid={`${testIdPrefix}-download`}
              onClick={() => handleDownload(SERVICE, attachmentUrl, attachmentName)}
              className="cursor-pointer"
              title={t("common.download")}
            >
              <FileDownloadOutlinedIcon />
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div data-testid="status-details-drawer" className="min-w-[400px]">
      <SideDrawer
        isOpen={isOpen}
        title={t("licensing.details.title")}
        onClose={() => onClose("detailsDrawer")}
        submitLabel={t("common.close")}
        onSubmit={() => onClose("detailsDrawer")}
        hideCancelButton
      >
        <div className="w-[400px] p-6">
          {hasApprovalDetails && (
            <div className="space-y-3" data-testid="status-details-approval">
              {taskDecision?.startDate && (
                <>
                  <div className="text-sm font-bold mb-1">{t("licensing.access-enabled-from")}</div>
                  <p className="text-sm">{formatDate(taskDecision.startDate, emptyValue)}</p>
                </>
              )}

              {taskDecision?.expiryDate && (
                <>
                  <div className="text-sm font-bold mb-1">
                    {t("licensing.access-enabled-until")}
                  </div>
                  <p className="text-sm">{formatDate(taskDecision.expiryDate, emptyValue)}</p>
                </>
              )}

              {isDefined(taskDecision?.numberOfAccountsAllowed) && (
                <>
                  <div className="text-sm font-bold mb-1">
                    {t("licensing.no-of-accounts-allowed")}
                  </div>
                  <p className="text-sm">{String(taskDecision?.numberOfAccountsAllowed)}</p>
                </>
              )}

              {taskDecision?.companyId && (
                <>
                  <div className="text-sm font-bold mb-1">{t("licensing.company-id")}</div>
                  <p className="text-sm break-all">{taskDecision.companyId}</p>
                </>
              )}

              {taskDecision?.approvalNotes && (
                <>
                  <div className="text-sm font-bold mb-1">{t("licensing.approval-notes")}</div>
                  <p className="text-sm whitespace-pre-wrap">{taskDecision.approvalNotes}</p>
                </>
              )}

              {approvalAttachmentUrl &&
                renderAttachment(approvalAttachmentUrl, "status-details-approval-attachment")}
            </div>
          )}

          {hasRejectionDetails && (
            <div
              className={`${hasApprovalDetails ? "mt-6 " : ""}space-y-3`}
              data-testid="status-details-rejection"
            >
              {taskDecision?.rejectionNotes && (
                <>
                  <div className="text-sm font-bold mb-1">{t("licensing.rejection-notes")}</div>
                  <p className="text-sm whitespace-pre-wrap">{taskDecision.rejectionNotes}</p>
                </>
              )}

              {rejectionAttachmentUrl &&
                renderAttachment(rejectionAttachmentUrl, "status-details-rejection-attachment")}
            </div>
          )}

          {!hasApprovalDetails && !hasRejectionDetails && (
            <div className="text-sm text-gray-500">
              {t("licensing.details.no-details", { defaultValue: "No details to show" })}
            </div>
          )}
        </div>
      </SideDrawer>
    </div>
  );
};

export default StatusDetailsDrawer;
